// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the rings of the ringed planets (owner, 2026-10-23: "for Saturn
 * and any other planet with rings add them to XR in the 3D view and AR").
 *
 * One table, read by both globes: the browser's MapLibre globe
 * (`planetRingsMapLayer.ts`) and the immersive globe in the headset and in
 * phone AR (`vrPlanetRings.ts`). A dataset's body comes from its
 * `celestialBody` (offEarthBody.ts); the four bodies with rings — Saturn,
 * Jupiter, Uranus and Neptune — get theirs drawn in the body's equatorial
 * plane, as a flat annulus around the globe, measured in equatorial radii
 * of the body so the rings sit where they sit around the real planet.
 *
 * Distances and widths are NASA / JPL ring facts (Cassini for Saturn,
 * Voyager for the others). The look is a 1-D profile across the rings —
 * colour and transparency by radius — built once per body
 * (`buildRingProfile`), deterministic, no picture downloaded: the whole
 * thing is a few hundred bytes of numbers. Narrow rings (Uranus's,
 * Neptune's, Saturn's F) are drawn no thinner than `MIN_BAND_R` so they
 * still show on a 400 px globe; their true widths are a few km.
 *
 * Nothing here is lit: a dataset is drawn flat (photorealEarth switches
 * the Sun off under a dataset), so the rings carry their own brightness.
 * Seen edge-on they vanish, as the real ones do.
 */

/** One band of a ring system, in equatorial radii of its planet. */
export interface RingBand {
  /** Inner edge, in planet radii. */
  readonly inner: number
  /** Outer edge, in planet radii (> inner). */
  readonly outer: number
  /** Colour, sRGB 0–255. */
  readonly rgb: readonly [number, number, number]
  /** Opacity at the inner edge, 0–1. */
  readonly alpha: number
  /** Opacity at the outer edge; absent = the same as `alpha`. */
  readonly alphaOuter?: number
  /** Fine ringlet grain across the band (Saturn's main rings). */
  readonly grain?: boolean
  /** The ring's common name, for tests and debugging. */
  readonly name: string
}

export interface RingSystem {
  /** The body, lower case, as offEarthBody.ts names it. */
  readonly body: string
  /** Equatorial radius of the body in km (the unit of every band). */
  readonly equatorialRadiusKm: number
  readonly bands: readonly RingBand[]
}

/**
 * The narrowest band drawn, in planet radii (about 200 km on Uranus, 1.3 px
 * on a 160 px globe): thinner rings would vanish. Uranus's and Neptune's
 * rings are a few km to a few tens of km wide, so they are drawn wider than
 * they are, as every picture of them is.
 */
export const MIN_BAND_R = 0.008

/** Texels across the profile texture, inner edge to outer edge. */
export const RING_PROFILE_WIDTH = 2048

/** Build a band from km, the way the sources give them. */
function km(
  equatorialRadiusKm: number,
  name: string,
  innerKm: number,
  outerKm: number,
  rgb: readonly [number, number, number],
  alpha: number,
  extra: { alphaOuter?: number; grain?: boolean } = {},
): RingBand {
  return { name, inner: innerKm / equatorialRadiusKm, outer: outerKm / equatorialRadiusKm, rgb, alpha, ...extra }
}

/** A narrow ring given by its centre and width in km. */
function narrow(
  equatorialRadiusKm: number,
  name: string,
  centreKm: number,
  widthKm: number,
  rgb: readonly [number, number, number],
  alpha: number,
): RingBand {
  return km(equatorialRadiusKm, name, centreKm - widthKm / 2, centreKm + widthKm / 2, rgb, alpha)
}

const SATURN_R = 60268
const JUPITER_R = 71492
const URANUS_R = 25559
const NEPTUNE_R = 24764

/**
 * The ring systems, by body (lower case). Saturn's D, C, B, Cassini
 * division, A (with the Encke gap) and F; Jupiter's halo, main and the two
 * gossamer rings; Uranus's nine narrow rings 6 to ε; Neptune's Galle,
 * Le Verrier, Lassell, Arago and Adams.
 */
export const RING_SYSTEMS: Readonly<Record<string, RingSystem>> = {
  saturn: {
    body: 'saturn',
    equatorialRadiusKm: SATURN_R,
    bands: [
      km(SATURN_R, 'D', 66900, 74510, [120, 112, 102], 0.04, { alphaOuter: 0.12 }),
      km(SATURN_R, 'C', 74658, 92000, [150, 140, 125], 0.22, { alphaOuter: 0.32, grain: true }),
      km(SATURN_R, 'B', 92000, 117580, [206, 186, 152], 0.78, { alphaOuter: 0.96, grain: true }),
      km(SATURN_R, 'Cassini division', 117580, 122170, [96, 90, 84], 0.14),
      km(SATURN_R, 'A', 122170, 133430, [192, 174, 142], 0.82, { grain: true }),
      // The Encke gap (325 km, nearly empty) splits the A ring.
      km(SATURN_R, 'A outer', 133750, 136775, [186, 168, 138], 0.78, { grain: true }),
      narrow(SATURN_R, 'F', 140180, 300, [200, 196, 188], 0.42),
    ],
  },
  jupiter: {
    body: 'jupiter',
    equatorialRadiusKm: JUPITER_R,
    bands: [
      km(JUPITER_R, 'halo', 92000, 122500, [172, 152, 132], 0.02, { alphaOuter: 0.07 }),
      km(JUPITER_R, 'main', 122500, 129000, [190, 170, 146], 0.24),
      km(JUPITER_R, 'Amalthea gossamer', 129000, 182000, [180, 160, 140], 0.06, { alphaOuter: 0.02 }),
      km(JUPITER_R, 'Thebe gossamer', 182000, 226000, [170, 152, 136], 0.02, { alphaOuter: 0 }),
    ],
  },
  uranus: {
    body: 'uranus',
    equatorialRadiusKm: URANUS_R,
    bands: [
      narrow(URANUS_R, '6', 41837, 2, [92, 92, 98], 0.5),
      narrow(URANUS_R, '5', 42234, 2, [92, 92, 98], 0.5),
      narrow(URANUS_R, '4', 42570, 3, [92, 92, 98], 0.5),
      narrow(URANUS_R, 'alpha', 44718, 7, [98, 98, 104], 0.6),
      narrow(URANUS_R, 'beta', 45661, 8, [98, 98, 104], 0.6),
      narrow(URANUS_R, 'eta', 47175, 2, [92, 92, 98], 0.45),
      narrow(URANUS_R, 'gamma', 47627, 3, [96, 96, 102], 0.55),
      narrow(URANUS_R, 'delta', 48300, 5, [96, 96, 102], 0.55),
      narrow(URANUS_R, 'lambda', 50023, 2, [90, 90, 96], 0.35),
      narrow(URANUS_R, 'epsilon', 51149, 200, [110, 110, 116], 0.85),
    ],
  },
  neptune: {
    body: 'neptune',
    equatorialRadiusKm: NEPTUNE_R,
    bands: [
      km(NEPTUNE_R, 'Galle', 40900, 42900, [140, 124, 114], 0.07),
      narrow(NEPTUNE_R, 'Le Verrier', 53200, 110, [150, 132, 120], 0.5),
      km(NEPTUNE_R, 'Lassell', 53200, 57200, [140, 124, 114], 0.06),
      narrow(NEPTUNE_R, 'Arago', 57200, 100, [146, 128, 116], 0.3),
      narrow(NEPTUNE_R, 'Adams', 62932, 50, [156, 136, 122], 0.65),
    ],
  },
}

/**
 * The ring system of a body named by a dataset (`celestialBody`, in any
 * case, or offEarthBody's lower-case name), or null: the Earth, a body
 * without rings, nothing.
 */
export function ringSystemOf(body: string | null | undefined): RingSystem | null {
  if (typeof body !== 'string') return null
  return RING_SYSTEMS[body.trim().toLowerCase()] ?? null
}

/** Inner and outer edge of the whole system in planet radii, with a little clear margin at each end. */
export function ringExtent(system: RingSystem): { inner: number; outer: number } {
  let inner = Infinity
  let outer = 0
  for (const b of system.bands) {
    const half = Math.max(0, MIN_BAND_R - (b.outer - b.inner)) / 2
    inner = Math.min(inner, b.inner - half)
    outer = Math.max(outer, b.outer + half)
  }
  const margin = (outer - inner) * 0.01
  return { inner: inner - margin, outer: outer + margin }
}

/**
 * Ringlet grain, deterministic by radius in km: 1 at its brightest, about
 * 0.8 in its troughs. Its periods (about 1 100, 2 800 and 5 700 km) stay
 * above a pixel on a 160 px globe (one pixel is about 500 km there), so
 * the grain reads as ringlets, not as moiré.
 */
function grainAt(radiusKm: number): number {
  const a = 0.5 + 0.5 * Math.sin(radiusKm / 180)
  const b = 0.5 + 0.5 * Math.sin(radiusKm / 450 + 1.3)
  const c = 0.5 + 0.5 * Math.sin(radiusKm / 900 + 0.4)
  return 1 - 0.12 * a * b - 0.08 * c * a
}

/**
 * The profile across the rings: `width` RGBA texels (straight alpha,
 * sRGB) from `inner` to `outer` planet radii. A band's edge is softened
 * over one texel; bands narrower than `MIN_BAND_R` are widened to it,
 * keeping their centre. Where bands overlap (Lassell under Le Verrier) the
 * more opaque one shows through the other.
 */
export function buildRingProfile(
  system: RingSystem,
  width = RING_PROFILE_WIDTH,
): { rgba: Uint8Array; inner: number; outer: number; width: number } {
  const { inner, outer } = ringExtent(system)
  const rgba = new Uint8Array(width * 4)
  const step = (outer - inner) / width
  const bands = system.bands.map(b => {
    const w = b.outer - b.inner
    const grow = Math.max(0, MIN_BAND_R - w) / 2
    return { ...b, lo: b.inner - grow, hi: b.outer + grow }
  })
  for (let i = 0; i < width; i++) {
    const r = inner + (i + 0.5) * step
    let acc = 0
    let rr = 0
    let gg = 0
    let bb = 0
    for (const b of bands) {
      // Coverage of this texel by the band, 0–1, soft over one texel at each edge.
      const cover = Math.max(0, Math.min(1, (r - b.lo) / step + 0.5)) * Math.max(0, Math.min(1, (b.hi - r) / step + 0.5))
      if (cover <= 0) continue
      const t = Math.max(0, Math.min(1, (r - b.inner) / Math.max(1e-9, b.outer - b.inner)))
      let a = (b.alpha + ((b.alphaOuter ?? b.alpha) - b.alpha) * t) * cover
      let k = 1
      if (b.grain) {
        k = grainAt(r * system.equatorialRadiusKm)
        a *= 0.9 + 0.1 * k
      }
      // "Over" compositing, straight alpha: the bands stack.
      const keep = 1 - a
      rr = rr * keep + b.rgb[0] * k * a
      gg = gg * keep + b.rgb[1] * k * a
      bb = bb * keep + b.rgb[2] * k * a
      acc = acc * keep + a
    }
    const o = i * 4
    if (acc > 0) {
      rgba[o] = Math.round(Math.min(255, rr / acc))
      rgba[o + 1] = Math.round(Math.min(255, gg / acc))
      rgba[o + 2] = Math.round(Math.min(255, bb / acc))
      rgba[o + 3] = Math.round(Math.min(1, acc) * 255)
    }
  }
  return { rgba, inner, outer, width }
}

/**
 * Is the point `p` (unit-sphere frame, the body's centre at the origin and
 * radius 1) hidden from a camera at `cam` by the body itself? Used on the
 * browser globe, where the rings are drawn without a depth buffer: the
 * segment camera→point must not pass through the sphere. The same test
 * runs in the layer's fragment shader.
 */
export function hiddenByBody(cam: readonly [number, number, number], p: readonly [number, number, number]): boolean {
  const vx = p[0] - cam[0]
  const vy = p[1] - cam[1]
  const vz = p[2] - cam[2]
  const vv = vx * vx + vy * vy + vz * vz
  if (vv === 0) return false
  const t = Math.min(1, Math.max(0, -(cam[0] * vx + cam[1] * vy + cam[2] * vz) / vv))
  const cx = cam[0] + t * vx
  const cy = cam[1] + t * vy
  const cz = cam[2] + t * vz
  return t < 1 && cx * cx + cy * cy + cz * cz < 1
}

/**
 * The annulus as a triangle strip in the body's frame (y north, the rings
 * in the x–z plane): for each of `segments + 1` angles an inner then an
 * outer vertex, `[x, y, z, t]` with t 0 at the inner edge and 1 at the
 * outer. Shared by both renderers so they draw the same ring.
 */
export function ringStrip(system: RingSystem, segments = 256): Float32Array {
  const { inner, outer } = ringExtent(system)
  const out = new Float32Array((segments + 1) * 2 * 4)
  let o = 0
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2
    const c = Math.cos(a)
    const s = Math.sin(a)
    out[o++] = inner * c
    out[o++] = 0
    out[o++] = inner * s
    out[o++] = 0
    out[o++] = outer * c
    out[o++] = 0
    out[o++] = outer * s
    out[o++] = 1
  }
  return out
}
